import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard } from "lucide-react";

export default function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [paying, setPaying] = useState(null);

  useEffect(() => { api.get("/portal/invoices").then((r) => setInvoices(r.data)).catch(() => {}); }, []);

  const pay = async (invoiceId) => {
    setPaying(invoiceId);
    try {
      const { data } = await api.post("/payments/invoice-checkout", {
        invoice_id: invoiceId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setPaying(null);
    }
  };

  return (
    <div data-testid="portal-invoices-page">
      <h1 className="font-heading font-bold text-2xl mb-6">Invoices & Payments</h1>
      <div className="space-y-4">
        {invoices.length === 0 && <p className="text-sm text-muted-foreground">No invoices yet.</p>}
        {invoices.map((inv) => (
          <div key={inv.id} data-testid={`portal-invoice-${inv.invoice_number}`} className="rounded-xl border bg-card p-6 card-lift">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-mono-cc text-sm text-[var(--cc-accent)]">{inv.invoice_number}</p>
                  <StatusBadge status={inv.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Issued {fmtDate(inv.created_at)}{inv.due_date ? ` · Due ${fmtDate(inv.due_date)}` : ""}{inv.paid_at ? ` · Paid ${fmtDate(inv.paid_at)}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <p className="font-heading font-bold text-xl">{fmtMoney(inv.total)}</p>
                {["open", "overdue"].includes(inv.status) && (
                  <Button data-testid={`portal-pay-button-${inv.invoice_number}`} disabled={paying === inv.id} onClick={() => pay(inv.id)}
                    className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
                    {paying === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Pay Now</>}
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t space-y-1.5">
              {inv.line_items.map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{li.description}{li.qty > 1 ? ` × ${li.qty}` : ""}</span>
                  <span>{fmtMoney(li.qty * li.unit_price)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs text-muted-foreground pt-1"><span>Tax (8.25%)</span><span>{fmtMoney(inv.tax)}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
